import { buildShareFile } from '../utils/shareFile'
import { resolveStreamFetchableUrl } from './videoDownload'

export const canWebShare = () =>
    typeof navigator !== 'undefined' && typeof navigator.share === 'function'

// Every boundary this crosses can refuse the file for a different reason — the
// rendition, the fetch, the browser's type allowlist, the share sheet itself —
// and all of them fail into the same silent URL share. Trace each one under a
// single prefix so a device log says which boundary gave way. Read them over
// USB: chrome://inspect on a desktop, filter on "[share]".
const shareLog = (step, detail) => console.log(`[share] ${step}`, detail)

/**
 * Hand a video to the platform share sheet, as a file if the platform will
 * take one and as a link if it will not.
 *
 * The bytes come from the Cloudflare MP4 rather than the Firebase Storage
 * original: the bucket's CORS policy is per-origin and does not cover the
 * public share page, so fetching the original there fails outright — the same
 * reason downloads go through Cloudflare (see `videoDownload.js`).
 *
 * Two costs are worth knowing about. Minting the rendition can take seconds,
 * and on some browsers the transient user activation from the click expires
 * while it happens — that surfaces as a `NotAllowedError` and falls through to
 * the link. And a share the user dismisses arrives as `AbortError`, which is a
 * *finished* share, not a failure: opening a second, text-only sheet on top of
 * it is itself a way to end up posting a bare URL.
 *
 * @param {object} opts
 * @param {object} opts.source `{ shareId }` or `{ kind, id }` for the
 *   Cloudflare copy. Omit to share the link without trying for a file.
 * @param {string} opts.url The link to fall back to.
 * @param {string} [opts.cachedDirect] `cfDirectUrl` from the document, if any.
 *   A remembered rendition skips the callable entirely, which is time the
 *   click's transient user activation does not have to spend.
 * @param {string} [opts.cfUid] The document's `cf.uid`.
 * @param {string} [opts.title] Share sheet title, also the file name's stem.
 * @param {function(string): void} [opts.onStatus] `'preparing'` while
 *   Cloudflare mints the rendition, then `'downloading'` while it is fetched.
 * @return {Promise<'file'|'url'|'unsupported'|'failed'>} How it went out.
 */
export async function shareVideo({ source, url, title, cachedDirect, cfUid, onStatus }) {
    if (!canWebShare()) return 'unsupported'

    const started = Date.now()
    const since = () => Date.now() - started

    let file = null
    if (source) {
        try {
            const mp4Url = await resolveStreamFetchableUrl({ source, cachedDirect, cfUid, onStatus })
            onStatus?.('downloading')
            const resp = await fetch(mp4Url)
            shareLog('fetch', {
                ok: resp.ok,
                status: resp.status,
                // An "opaque" response has no readable body — the blob would be
                // empty and the share would carry nothing.
                responseType: resp.type,
                contentType: resp.headers.get('content-type'),
                contentLength: resp.headers.get('content-length'),
                ms: since(),
            })
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
            const blob = await resp.blob()
            file = buildShareFile(blob, title)
            shareLog('file built', {
                blobType: blob.type,
                blobSize: blob.size,
                name: file.name,
                type: file.type,
                ms: since(),
            })
        } catch (err) {
            shareLog('file unavailable', { name: err.name, message: err.message, ms: since() })
        }
    }

    if (file) {
        let allowed = false
        try {
            allowed = typeof navigator.canShare === 'function'
                && navigator.canShare({ files: [file] })
        } catch (err) {
            shareLog('canShare threw', { name: err.name, message: err.message })
        }
        shareLog('canShare(files)', { allowed, ms: since() })

        if (allowed) {
            try {
                await navigator.share({ files: [file], title })
                shareLog('share(files) ok', { ms: since() })
                return 'file'
            } catch (err) {
                shareLog('share(files) failed', { name: err.name, message: err.message, ms: since() })
                if (err.name === 'AbortError') return 'file'
            }
        }
    }

    shareLog('falling back to URL share', { url, ms: since() })
    try {
        await navigator.share({ title, url })
        shareLog('share(url) ok', { ms: since() })
        return 'url'
    } catch (err) {
        shareLog('share(url) failed', { name: err.name, message: err.message, ms: since() })
        return err.name === 'AbortError' ? 'url' : 'failed'
    }
}
